import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { MgtPerson } from "@microsoft/mgt-react";
import { left } from "@popperjs/core";
import React, { useReducer, useState } from "react";
import StatusCode from "status-code-enum";
import Swal from "sweetalert2";
import { apiScopes } from "../authConfig";
import { ChangeType } from "../enums";
import { errorsTypes } from "../errorTypes";
import { directoryObject } from "../objects/directoryObj";
import { resource } from "../objects/resource";
import {
  addSPOSiteOwner,
  getSPOSiteGuests,
  removeGuestFromWorkspace,
  removeSPOSiteOwner,
} from "../services/resource-manager";
import { ConvertBytesToMB, ConvertDateStringToLongDate } from "../utils";
import M365Card from "./m365-card";
import SPOSiteCard from "./spo-site-card";
import { toast } from "react-toastify";
import { OwnerManager } from "./owner-manager";
import { GuestManager } from "./guest-manager";

interface resourceDetailProps {
  ownedResource: resource;
  tabIndex: number;
  baseTabId: string;
  refreshDatasetFunction: (
    resourceID: string,
    changeType: ChangeType,
    value: any
  ) => void;
}

/**
 * Represents the details of a single M365 or SPO resources, displayed by the resources grid
 */
export function ResourceDetailSPO(props: resourceDetailProps) {
  const [guests, setGuests] = useState<directoryObject[]>([]);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  MgtPerson.config.useContactApis = true;
  const { instance, accounts } = useMsal();
  
  const accessTokenRequest = {
    scopes: apiScopes.resourceManagementScopes,
    account: accounts[0],
  };

  // Common styles for activity section
  const labelStyle: React.CSSProperties = {
    fontWeight: "bold",
    textAlign: "right",
    paddingRight: "8px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  };

  const inputStyle: React.CSSProperties = {
    width: "100%"
  };

  const rowStyle: React.CSSProperties = {
    marginBottom: "12px",
    alignItems: "center"
  };

  const ActivitySection = ({ title, backgroundColor, children }: { 
    title: string; 
    backgroundColor: string; 
    children: React.ReactNode; 
  }) => (
    <table className="table table-bordered">
      <tbody>
        <tr style={{ backgroundColor, color: "white" }}>
          <th>{title}</th>
        </tr>
        <tr style={{ backgroundColor: "white", textAlign: "justify" }}>
          <td>
            <div style={{ padding: "10px" }}>
              {children}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );

  // [Keep all existing async functions unchanged]
  async function loadGuests(resourceId: string): Promise<void> {
    Swal.showLoading();
    try {
      const accessTokenResponse =
        await instance.acquireTokenSilent(accessTokenRequest);
      let guestList = await getSPOSiteGuests(
        accessTokenResponse.accessToken,
        props.ownedResource.siteId
      );
      setGuests(guestList);
      Swal.close();
    } catch (error) {
      Swal.close();
      if (error instanceof InteractionRequiredAuthError) {
        Swal.fire({
          titleText: errorsTypes.sessionExpired.errorTitle,
          text: errorsTypes.sessionExpired.errorSubtitle,
          icon: "error",
          willClose: () => {
            instance.acquireTokenRedirect(accessTokenRequest);
          },
        });
      }
      console.log(error);
    }
  }

  async function addSiteAdministrator(
    ownerList: directoryObject[],
    resourceId: string
  ): Promise<void> {
    for (const currOwner of ownerList) {
      Swal.showLoading();
      try {
        const accessTokenResponse =
          await instance.acquireTokenSilent(accessTokenRequest);

        const toastLeaseRenewId = toast.loading(
          `Adding ${currOwner.userPrincipalName}`
        );
        const operationResult = await addSPOSiteOwner(
          accessTokenResponse.accessToken,
          resourceId,
          currOwner.userPrincipalName as string
        );

        Swal.close();

        if (
          operationResult === StatusCode.SuccessOK ||
          operationResult === StatusCode.SuccessNoContent
        ) {
          props.refreshDatasetFunction(
            props.ownedResource.siteUrl,
            operationResult === StatusCode.SuccessOK
              ? ChangeType.AddOwner
              : ChangeType.AddNonValidOwner,
            currOwner.userPrincipalName as string
          );

          forceUpdate();

          toast.update(toastLeaseRenewId, {
            render: "Operation Complete",
            type: "success",
            isLoading: false,
            autoClose: 8000,
          });
        } else {
          toast.error(errorsTypes.somethingWentWrong.errorSubtitle, {
            type: "error",
            isLoading: false,
            autoClose: 8000,
          });
        }
      } catch (error) {
        Swal.close();
        if (error instanceof InteractionRequiredAuthError) {
          Swal.fire({
            titleText: errorsTypes.sessionExpired.errorTitle,
            text: errorsTypes.sessionExpired.errorSubtitle,
            icon: "error",
            willClose: () => {
              instance.acquireTokenRedirect(accessTokenRequest);
            },
          });
        }
        console.log(error);
      }
    }
  }

  async function removeSiteAdministrator(
    ownerList: directoryObject[],
    resourceId: string
  ): Promise<void> {
    for (const currOwner of ownerList) {
      Swal.showLoading();
      try {
        const accessTokenResponse =
          await instance.acquireTokenSilent(accessTokenRequest);

        const toastLeaseRenewId = toast.loading(
          `Removing ${currOwner.userPrincipalName}`
        );
        const operationResult = await removeSPOSiteOwner(
          accessTokenResponse.accessToken,
          resourceId,
          currOwner.userPrincipalName as string
        );

        Swal.close();

        if (
          operationResult === StatusCode.SuccessOK ||
          operationResult === StatusCode.SuccessNoContent
        ) {
          const currentUserEmail = (
            (window as any).CURRENT_USER_EMAIL as string
          )
            .trim()
            .toLowerCase();
          const ownerEmail = (currOwner.userPrincipalName as string)
            .trim()
            .toLowerCase();

          if (currentUserEmail === ownerEmail) {
            // ✅ If removing current user, delete site
            props.refreshDatasetFunction(
              props.ownedResource.siteUrl,
              ChangeType.DeleteSite,
              currOwner.userPrincipalName as string
            );
          } else {
            // ✅ Otherwise, just remove owner
            props.refreshDatasetFunction(
              props.ownedResource.siteUrl,
              operationResult === StatusCode.SuccessOK
                ? ChangeType.RemoveOwner
                : ChangeType.RemoveNonValidOwner,
              currOwner.userPrincipalName as string
            );

            forceUpdate();
          }

          toast.update(toastLeaseRenewId, {
            render: "Operation Complete",
            type: "success",
            isLoading: false,
            autoClose: 8000,
          });
        } else {
          toast.error(errorsTypes.somethingWentWrong.errorSubtitle, {
            type: "error",
            isLoading: false,
            autoClose: 8000,
          });
        }
      } catch (error) {
        Swal.close();
        if (error instanceof InteractionRequiredAuthError) {
          Swal.fire({
            titleText: errorsTypes.sessionExpired.errorTitle,
            text: errorsTypes.sessionExpired.errorSubtitle,
            icon: "error",
            willClose: () => {
              instance.acquireTokenRedirect(accessTokenRequest);
            },
          });
        }
        console.log(error);
      }
    }
  }

  async function removeSiteGuest(
    ownerList: directoryObject[],
    resourceId: string
  ): Promise<void> {
    const currentUserEmail = (window as any).CURRENT_USER_EMAIL;
    Swal.showLoading();

    try {
      const accessTokenResponse =
        await instance.acquireTokenSilent(accessTokenRequest);

      // ✅ Remove other owners sequentially
      for (const owner of ownerList) {
        const toastLeaseRenewId = toast.loading(
          `Removing ${owner.userPrincipalName}`
        );
        const operationResult = await removeGuestFromWorkspace(
          accessTokenResponse.accessToken,
          resourceId,
          false,
          owner.id as string
        );
        Swal.close();

        if (
          operationResult === StatusCode.SuccessOK ||
          operationResult === StatusCode.SuccessNoContent
        ) {
          setGuests(guests.filter(o=>o.mail !== owner.userPrincipalName));
          toast.update(toastLeaseRenewId, {
            render: "Operation Complete",
            type: "success",
            isLoading: false,
            autoClose: 8000,
          });
        } else {
          toast.error(errorsTypes.somethingWentWrong.errorSubtitle, {
            type: "error",
            isLoading: false,
            autoClose: 8000,
          });
        }
      }
    } catch (error) {
      Swal.close();
      if (error instanceof InteractionRequiredAuthError) {
        Swal.fire({
          titleText: errorsTypes.sessionExpired.errorTitle,
          text: errorsTypes.sessionExpired.errorSubtitle,
          icon: "error",
          willClose: () => {
            instance.acquireTokenRedirect(accessTokenRequest);
          },
        });
      }
      console.log(error);
    }
  }

  return (
    <div style={{ marginLeft: "10px", marginRight: "10px" }}>
      <div className="row">
        <ul className="nav nav-tabs">
          <li
            className="nav-item"
            role="presentation"
            style={{ borderColor: "#E9E9E9" }}
          >
            <button
              type="button"
              id={props.baseTabId + "-overview"}
              className="nav-link active nav-link-resource"
              data-bs-toggle="tab"
              data-bs-target={"#" + props.baseTabId + "-overview-content"}
              role="tab"
              aria-controls="home"
              aria-selected="true"
              style={{ fontWeight: "bold" }}
            >
              <i
                className="intelicon-contacts-outlined"
                style={{ fontWeight: "bold" }}
              ></i>{" "}
              Overview
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              type="button"
              id={props.baseTabId + "-owners"}
              className="nav-link nav-link-resource"
              data-bs-toggle="tab"
              data-bs-target={"#" + props.baseTabId + "-owners-content"}
              role="tab"
              aria-controls="profile"
              aria-selected="false"
              style={{ fontWeight: "bold" }}
            >
              <i
                className="intelicon-primary-user-person-solid"
                style={{ fontWeight: "bold" }}
              ></i>{" "}
              Site Collection Administrators
            </button>
          </li>
          <li 
            className="nav-item" 
            role="presentation" 
            style={{
              display: props.ownedResource && 
                props.ownedResource.sensitivityLabel.trim() === "Internal - External" 
                ? "grid" 
                : "none"
            }}
          >
            <button
              type="button"
              id={props.baseTabId + "-guests"}
              className="nav-link nav-link-resource"
              data-bs-toggle="tab"
              data-bs-target={"#" + props.baseTabId + "-guests-content"}
              onClick={(e: any) => loadGuests(props.ownedResource.siteId)}
              role="tab"
              aria-controls="guests"
              aria-selected="false"
              style={{ fontWeight: "bold" }}
            >
              <i
                className="intelicon-users-people-solid"
                style={{ fontWeight: "bold" }}
              ></i>{" "}
              Guests
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              type="button"
              id={props.baseTabId + "-activity"}
              className="nav-link nav-link-resource"
              data-bs-toggle="tab"
              data-bs-target={"#" + props.baseTabId + "-activity-content"}
              role="tab"
              aria-controls="profile"
              aria-selected="false"
              style={{ fontWeight: "bold" }}
            >
              <i
                className="intelicon-activity-outlined"
                style={{ fontWeight: "bold" }}
              ></i>{" "}
              Activity
            </button>
          </li>
        </ul>
        <div
          className="tab-content border-end border-bottom border-start"
          style={{ backgroundColor: "#E9E9E9" }}
        >
          <div
            className="tab-pane fade active show "
            id={props.baseTabId + "-overview-content"}
            role="tabpanel"
            aria-labelledby="home-tab"
          >
            {props.ownedResource.isGroupConnected ? (
              <M365Card ownedResource={props.ownedResource} />
            ) : (
              <SPOSiteCard ownedResource={props.ownedResource} />
            )}
            <br />
          </div>
          
          <div
            className="tab-pane"
            id={props.baseTabId + "-owners-content"}
            role="tabpanel"
            aria-labelledby="profile-tab"
          >
            <div style={{ paddingLeft: "14px" }}>
              <br />
              <OwnerManager
                currentOwners={props.ownedResource.owners}
                resourceId={props.ownedResource.siteUrl}
                siteId={props.ownedResource.siteId}
                isM365={false}
                addFunction={addSiteAdministrator}
                removeFunction={removeSiteAdministrator}
                isAGS={props.ownedResource.agsTracked}
                currentNonExistingOwners={props.ownedResource.nonExistingOwners}
                currentNonValidOwners={props.ownedResource.nonValidOwners}
                readOnlyOwners={[]}
              />
              <br />
            </div>
          </div>
          
          <div
            className="tab-pane"
            id={props.baseTabId + "-guests-content"}
            role="tabpanel"
            aria-labelledby="profile-tab"
          >
            <div style={{ paddingLeft: "14px" }}>
              <br />
              <GuestManager
                currentOwners={guests}
                resourceId={props.ownedResource.siteId}
                isM365={false}
                addFunction={addSiteAdministrator}
                removeFunction={removeSiteGuest}
                isAGS={props.ownedResource.agsTracked}
                ownerCount={props.ownedResource.owners.length}
                refreshDatasetFunction={props.refreshDatasetFunction}
                spoSiteUrl={props.ownedResource.siteUrl}
              />
              <br />
            </div>
          </div>
          
          <div
            className="tab-pane"
            id={props.baseTabId + "-activity-content"}
            role="tabpanel"
            aria-labelledby="profile-tab"
          >
            {/* No Activity Available */}
            <div
              style={{
                display: !props.ownedResource.activityDataAvailable === false
                  ? "none"
                  : "grid",
              }}
            >
              <br />
              <div
                className="alert alert-light"
                role="alert"
                style={{ backgroundColor: "white" }}
              >
                <h4 className="alert-heading">
                  No activity was found for this space
                </h4>
                <p></p>
                <hr />
                <p className="mb-0">
                  Collaboration Spaces created 24 hours ago or less might not
                  have activity records available, please wait 24-48 hours for
                  the information to be available
                </p>
              </div>
            </div>

            {/* Activity Data Available */}
            <div
              style={{
                display: props.ownedResource.activityDataAvailable === false
                  ? "none"
                  : "grid",
              }}
            >
              <br />
              
              {/* SharePoint Activity Summary */}
              <ActivitySection 
                title="SharePoint Activity Summary" 
                backgroundColor="#077568"
              >
                <div className="row" style={rowStyle}>
                  <div className="col-3 col-lg-2">
                    <label className="col-form-label" style={labelStyle}>
                      Last Activity:
                    </label>
                  </div>
                  <div className="col-3 col-lg-4">
                    <input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      disabled
                      value={
                        new Date(props.ownedResource.lastUpdatedFormatted).getFullYear() < 2000
                          ? "Inactive for 5+ years"
                          : new Date(props.ownedResource.lastUpdatedFormatted).toDateString()
                      }
                    />
                  </div>
                  <div className="col-3 col-lg-2">
                    <label className="col-form-label" style={labelStyle}>
                      Storage Used:
                    </label>
                  </div>
                  <div className="col-3 col-lg-4">
                    <input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      disabled
                      value={
                        ConvertBytesToMB(
                          props.ownedResource.siteActivity?.storageUsedByte as string
                        ) + " MB"
                      }
                    />
                  </div>
                </div>

                <div className="row" style={rowStyle}>
                  <div className="col-3 col-lg-2">
                    <label className="col-form-label" style={labelStyle}>
                      File Count:
                    </label>
                  </div>
                  <div className="col-3 col-lg-4">
                    <input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      disabled
                      value={props.ownedResource.siteActivity?.fileCount}
                    />
                  </div>
                  <div className="col-3 col-lg-2">
                    <label className="col-form-label" style={labelStyle}>
                      Active Files:
                    </label>
                  </div>
                  <div className="col-3 col-lg-4">
                    <input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      disabled
                      value={props.ownedResource.siteActivity?.activeFileCount}
                    />
                  </div>
                </div>

                <div className="row" style={rowStyle}>
                  <div className="col-3 col-lg-2">
                    <label className="col-form-label" style={labelStyle}>
                      Page Views:
                    </label>
                  </div>
                  <div className="col-3 col-lg-4">
                    <input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      disabled
                      value={props.ownedResource.siteActivity?.pageViewCount}
                    />
                  </div>
                  <div className="col-3 col-lg-2">
                    {/* Empty column for spacing */}
                  </div>
                  <div className="col-3 col-lg-4">
                    {/* Empty column for spacing */}
                  </div>
                </div>
              </ActivitySection>
              
              <br />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResourceDetailSPO;